import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MultiSelectButtonComponent } from '../multi-select-button/multi-select-button.component';
import { SelectTriggerTemplateDirective } from '../multi-select-button/multi-select-template.directives';
import { IconComponent } from '../icon/icon.component';
import {
  type SelectAction,
  type SelectActionEvent,
  type SelectItem,
  type SelectAvatar,
} from '../multi-select-button/multi-select-button.types';

export interface AssignMember {
  readonly id: string;
  readonly name: string;
  readonly avatarUrl?: string;
  readonly initials?: string;
  readonly avatarTone?: SelectAvatar['tone'];
  readonly avatarBackground?: string;
  readonly avatarText?: string;
}

export interface AssignCapabilities {
  readonly canAssign: boolean;
  readonly canResolve: boolean;
  readonly canReopen: boolean;
}

export type AssignStatus = 'draft' | 'open' | 'resolved';
export type AssignWorkspaceKind = 'team' | 'personal';

@Component({
  selector: 'app-assign-to-button',
  imports: [MultiSelectButtonComponent, SelectTriggerTemplateDirective, IconComponent],
  templateUrl: './assign-to-button.component.html',
  styleUrl: './assign-to-button.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignToButtonComponent {
  public readonly workspaceKind = input.required<AssignWorkspaceKind>();
  public readonly members = input<readonly AssignMember[]>([]);
  public readonly selectedMembers = input<readonly AssignMember[]>([]);
  public readonly currentUserId = input.required<string>();
  public readonly assigneeIds = input<readonly string[]>([]);
  public readonly capabilities = input.required<AssignCapabilities>();
  public readonly status = input<AssignStatus>('draft');
  public readonly itemLabel = input('item');
  public readonly pending = input(false);
  public readonly loading = input(false);
  public readonly errorText = input<string | null>(null);
  public readonly searchMode = input<'local' | 'external'>('local');
  public readonly hasMore = input(false);
  public readonly loadingMore = input(false);
  public readonly allowReadOnlyEmpty = input(false);
  /** The trigger text while nobody is assigned; a host with a second "Assign" control names this one differently. */
  public readonly emptyLabel = input('Assign');

  public readonly assigneeIdsChange = output<readonly string[]>();
  public readonly resolve = output<void>();
  public readonly reopen = output<void>();
  public readonly retry = output<void>();
  public readonly searchChange = output<string>();
  public readonly loadMore = output<void>();

  protected readonly canAssign = computed(() => this.capabilities().canAssign);
  protected readonly assignedToSelf = computed(() => this.assigneeIds().includes(this.currentUserId()));
  protected readonly triggerLabel = computed(() => {
    if (this.status() === 'resolved') return 'Resolved';
    const ids = this.assigneeIds();
    if (!ids.length) return this.emptyLabel();
    if (ids.length === 1 && this.assignedToSelf()) return 'Assigned to me';
    if (ids.length === 1) {
      return (
        this.selectedMembers().find((member) => member.id === ids[0])?.name ??
        this.members().find((member) => member.id === ids[0])?.name ??
        '1 assignee'
      );
    }
    if (this.assignedToSelf() && ids.length > 3) return `Assigned to me + ${ids.length - 1} others`;
    return `${ids.length} assignees`;
  });
  protected readonly triggerAvatars = computed(() =>
    this.assigneeIds()
      .slice(0, 3)
      .map((id) => {
        const member =
          this.selectedMembers().find((candidate) => candidate.id === id) ??
          this.members().find((candidate) => candidate.id === id);
        return this.avatarFor(member);
      }),
  );
  protected readonly readonlySelection = computed(() => !this.canAssign());
  protected readonly triggerDisabled = computed(
    () =>
      this.workspaceKind() === 'personal' ||
      (!this.allowReadOnlyEmpty() &&
        !this.canAssign() &&
        !this.assigneeIds().length &&
        !this.capabilities().canResolve &&
        !this.capabilities().canReopen),
  );
  protected readonly selfItem = computed<readonly SelectItem[]>(() => {
    if (!this.canAssign() && !this.assignedToSelf()) return [];
    const member =
      this.selectedMembers().find((candidate) => candidate.id === this.currentUserId()) ??
      this.members().find((candidate) => candidate.id === this.currentUserId());
    return [
      {
        id: this.currentUserId(),
        label: this.assignedToSelf() ? 'Assigned to me' : 'Assign to me',
        avatar: this.avatarFor(member),
      },
    ];
  });
  protected readonly otherItems = computed<readonly SelectItem[]>(() => {
    const assigned = new Set(this.assigneeIds());
    return this.members()
      .filter((member) => member.id !== this.currentUserId() && (this.canAssign() || assigned.has(member.id)))
      .map((member) => ({ id: member.id, label: member.name, avatar: this.avatarFor(member) }));
  });
  protected readonly topText = computed<string | null>(() =>
    !this.canAssign() && this.assignedToSelf() ? this.explanation() : null,
  );
  protected readonly bottomText = computed<string | null>(() =>
    !this.canAssign() && !this.assignedToSelf() && this.assigneeIds().length ? this.explanation() : null,
  );
  protected readonly action = computed<SelectAction | null>(() => {
    if (this.status() === 'draft') return null;
    if (this.status() === 'open' && this.capabilities().canResolve) {
      return { id: 'resolve', label: 'Mark as resolved', pending: this.pending() };
    }
    if (this.status() === 'resolved' && this.capabilities().canReopen) {
      return { id: 'reopen', label: 'Reopen', pending: this.pending() };
    }
    return null;
  });

  protected onAction(event: SelectActionEvent): void {
    if (event.actionId === 'resolve') this.resolve.emit();
    if (event.actionId === 'reopen') this.reopen.emit();
  }

  private explanation(): string {
    return `Only the owner of this ${this.itemLabel()} can assign it to other team members`;
  }

  private avatarFor(member: AssignMember | undefined): SelectAvatar {
    const name = member?.name ?? 'You';
    const initials =
      member?.initials ??
      name
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part.slice(0, 1))
        .join('')
        .toLocaleUpperCase();
    return {
      initials,
      imageUrl: member?.avatarUrl,
      tone: member?.avatarTone,
      backgroundColor: member?.avatarBackground,
      textColor: member?.avatarText,
    };
  }
}
